import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LocaleContext';
import { resolveMediaObject } from '../lib/bookingMedia';
import {
  RECEPTION_FUEL_LEVELS,
  RECEPTION_PHOTO_SIDES,
  RECEPTION_REQUIRED_SIDES,
  emptyDamageDiagram,
  normalizeDamageDiagram,
  normalizeReceptionPhotos,
  receptionIsComplete,
  validateReceptionDraft,
} from '../lib/receptionContract';
import { supabase } from '../lib/supabase';

function SignatureCaptureModal({ open, onClose, onConfirm, t }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasStroke, setHasStroke] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setHasStroke(false);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const canvas = canvasRef.current;
    if (!canvas) {
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const ratio = window.devicePixelRatio || 1;
      const width = parent.clientWidth;
      const height = parent.clientHeight;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.6;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#111';
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, width, height);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  function pos(event) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = event.touches?.[0] || event;
    return {
      x: point.clientX - rect.left,
      y: point.clientY - rect.top,
    };
  }

  function start(event) {
    event.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pos(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function move(event) {
    if (!drawing.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pos(event);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasStroke(true);
  }

  function end() {
    drawing.current = false;
  }

  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight);
    setHasStroke(false);
  }

  function confirm() {
    if (!hasStroke) return;
    onConfirm?.(canvasRef.current.toDataURL('image/png'));
    onClose?.();
  }

  return (
    <div className="reception-signature-modal" role="dialog" aria-modal="true">
      <header className="reception-signature-modal-bar">
        <strong>{t('reception.signature')}</strong>
        <div className="row-actions">
          <button type="button" className="ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="ghost"
            disabled={!hasStroke}
            onClick={clear}
          >
            {t('reception.clearSignature')}
          </button>
          <button
            type="button"
            className="primary"
            disabled={!hasStroke}
            onClick={confirm}
          >
            {t('reception.done')}
          </button>
        </div>
      </header>
      <div className="reception-signature-modal-pad">
        <canvas
          ref={canvasRef}
          className="reception-signature-modal-canvas"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
      </div>
    </div>
  );
}

function DamageDiagramPad({ diagram, disabled, onChange, t }) {
  function addMark(event) {
    if (disabled || diagram.noDamage) return;
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    onChange({
      ...diagram,
      bodyType: 'sedan',
      noDamage: false,
      marks: [
        ...diagram.marks,
        {
          id: crypto.randomUUID(),
          zone: 'body',
          x: Math.min(0.98, Math.max(0.02, x)),
          y: Math.min(0.98, Math.max(0.02, y)),
          note: '',
        },
      ],
    });
  }

  return (
    <div className="reception-diagram-block">
      <label className="reception-check">
        <input
          type="checkbox"
          disabled={disabled}
          checked={!!diagram.noDamage}
          onChange={(e) =>
            onChange({
              ...diagram,
              bodyType: 'sedan',
              noDamage: e.target.checked,
              marks: e.target.checked ? [] : diagram.marks,
            })
          }
        />
        {t('reception.noDamage')}
      </label>
      <p className="lede" style={{ marginTop: 8 }}>{t('reception.tapDamage')}</p>
      <button
        type="button"
        className="reception-blueprint-image-pad"
        disabled={disabled || diagram.noDamage}
        onClick={addMark}
      >
        <img
          src="/reception/damage_blueprint.png"
          alt=""
          className="reception-blueprint-image"
          draggable={false}
        />
        {(diagram.marks || []).map((mark) => (
          <span
            key={mark.id}
            className="reception-mark"
            style={{ left: `${mark.x * 100}%`, top: `${mark.y * 100}%` }}
            title={mark.note || t('reception.damageMark')}
          />
        ))}
      </button>
      {diagram.marks.length > 0 && !disabled ? (
        <div className="row-actions">
          <button
            type="button"
            className="ghost"
            onClick={() =>
              onChange({
                ...diagram,
                bodyType: 'sedan',
                marks: diagram.marks.slice(0, -1),
              })
            }
          >
            {t('reception.undoMark')}
          </button>
          <button
            type="button"
            className="ghost"
            onClick={() => onChange({ ...diagram, bodyType: 'sedan', marks: [] })}
          >
            {t('reception.clearMarks')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function VehicleReceptionPanel({ booking, onCompleted }) {
  const t = useT();
  const { profile } = useAuth();
  const [jobCard, setJobCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingSides, setUploadingSides] = useState({});
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [mileage, setMileage] = useState('');
  const [fuelLevel, setFuelLevel] = useState('');
  const [photos, setPhotos] = useState([]);
  const [diagram, setDiagram] = useState(emptyDamageDiagram());
  const [accessories, setAccessories] = useState('');
  const [warningLights, setWarningLights] = useState('');
  const [vin, setVin] = useState('');
  const [vinUnavailable, setVinUnavailable] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState('');
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [photoUrls, setPhotoUrls] = useState({});
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const locked = receptionIsComplete(jobCard);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data: cardId, error: ensureError } = await supabase.rpc(
      'ensure_job_card_for_booking_id',
      { p_booking_id: booking.id },
    );
    if (ensureError) {
      setLoading(false);
      setError(ensureError.message);
      return;
    }
    const { data, error: loadError } = await supabase
      .from('job_cards')
      .select(
        'id, checkin_mileage, checkin_fuel_level, checkin_photos, damage_diagram, accessories_notes, warning_lights, signature_url, vin_unavailable, reception_completed_at, received_by',
      )
      .eq('id', cardId)
      .maybeSingle();
    if (loadError) {
      setLoading(false);
      setError(loadError.message);
      return;
    }
    setJobCard(data);
    setMileage(
      data?.checkin_mileage != null
        ? String(data.checkin_mileage)
        : booking.vehicles?.mileage
          ? String(booking.vehicles.mileage)
          : '',
    );
    setFuelLevel(data?.checkin_fuel_level || '');
    setPhotos(normalizeReceptionPhotos(data?.checkin_photos));
    setDiagram(normalizeDamageDiagram(data?.damage_diagram));
    setAccessories(data?.accessories_notes || '');
    setWarningLights(data?.warning_lights || '');
    setSignatureUrl(data?.signature_url || '');
    setVinUnavailable(!!data?.vin_unavailable);
    setVin(booking.vehicles?.vin || '');
    setLoading(false);
  }, [booking.id, booking.vehicles?.mileage, booking.vehicles?.vin]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let alive = true;
    async function resolve() {
      const next = {};
      for (const photo of photos) {
        const key = `${photo.bucket}:${photo.path}`;
        try {
          const resolved = await resolveMediaObject(photo);
          next[key] = resolved?.url || null;
        } catch {
          next[key] = null;
        }
      }
      if (alive) setPhotoUrls(next);
    }
    resolve();
    return () => {
      alive = false;
    };
  }, [photos]);

  async function uploadSide(side, fileList) {
    const file = fileList?.[0];
    if (!file || locked || uploadingSides[side]) return;
    setUploadingSides((current) => ({ ...current, [side]: true }));
    setError('');
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError || !authData?.user?.id) {
      setUploadingSides((current) => {
        const next = { ...current };
        delete next[side];
        return next;
      });
      setError(authError?.message || t('reception.errAuth'));
      return;
    }
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `${authData.user.id}/reception/${booking.id}/${side}-${crypto.randomUUID()}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('vehicle-photos')
      .upload(path, file, { upsert: false });
    setUploadingSides((current) => {
      const next = { ...current };
      delete next[side];
      return next;
    });
    if (uploadError) {
      setError(uploadError.message);
      return;
    }
    setPhotos((current) => {
      const without = current.filter((item) => item.side !== side);
      return [
        ...without,
        { side, bucket: 'vehicle-photos', path, name: file.name },
      ];
    });
  }

  async function uploadSignatureIfNeeded() {
    if (!signatureDataUrl || signatureDataUrl === signatureUrl) {
      return signatureUrl;
    }
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError || !authData?.user?.id) {
      throw new Error(authError?.message || t('reception.errAuth'));
    }
    const blob = await (await fetch(signatureDataUrl)).blob();
    const path = `${authData.user.id}/reception/${booking.id}/signature-${crypto.randomUUID()}.png`;
    const { error: uploadError } = await supabase.storage
      .from('documents')
      .upload(path, blob, { contentType: 'image/png', upsert: false });
    if (uploadError) throw uploadError;
    setSignatureUrl(path);
    return path;
  }

  async function save({ finalize }) {
    if (locked) return;
    const draft = {
      mileage,
      fuelLevel,
      photos,
      diagram,
      vin,
      vinUnavailable,
      signatureUrl: signatureDataUrl || signatureUrl,
      finalize,
    };
    const errors = validateReceptionDraft(draft);
    if (errors.length) {
      setError(t(errors[0]));
      return;
    }
    setSaving(true);
    setError('');
    setOk('');
    try {
      const sig = finalize
        ? await uploadSignatureIfNeeded()
        : signatureUrl || null;
      const { data, error: rpcError } = await supabase.rpc(
        'save_vehicle_reception',
        {
          p_booking_id: booking.id,
          p_mileage: Number(mileage),
          p_fuel_level: fuelLevel,
          p_photos: photos,
          p_damage_diagram: diagram,
          p_accessories_notes: accessories || null,
          p_warning_lights: warningLights || null,
          p_signature_url: sig,
          p_vin: vinUnavailable ? null : vin.trim() || null,
          p_vin_unavailable: vinUnavailable,
          p_finalize: finalize,
        },
      );
      if (rpcError) throw rpcError;
      setJobCard(data);
      setOk(finalize ? t('reception.finalized') : t('reception.saved'));
      if (finalize) onCompleted?.(data);
    } catch (err) {
      setError(err?.message || t('reception.errSave'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="empty">{t('common.loading')}</p>;

  const complete = receptionIsComplete(jobCard);

  return (
    <div className="reception-panel">
      <header className="reception-head">
        <div>
          <strong>{t('reception.title')}</strong>
          <p className="lede" style={{ marginTop: 6 }}>
            {t('reception.lede')}
          </p>
        </div>
        {complete ? (
          <em className="pill status-completed">{t('reception.complete')}</em>
        ) : (
          <em className="pill">{t('reception.draft')}</em>
        )}
      </header>

      {error ? <p className="form-error">{error}</p> : null}
      {ok ? <p className="form-ok">{ok}</p> : null}
      {locked ? <p className="lede">{t('reception.locked')}</p> : null}

      <div className="form-row">
        <label>
          {t('reception.mileage')}
          <input
            type="number"
            min="0"
            disabled={locked}
            value={mileage}
            onChange={(e) => setMileage(e.target.value)}
          />
        </label>
        <label>
          {t('reception.fuel')}
          <select
            disabled={locked}
            value={fuelLevel}
            onChange={(e) => setFuelLevel(e.target.value)}
          >
            <option value="">{t('reception.pickFuel')}</option>
            {RECEPTION_FUEL_LEVELS.map((level) => (
              <option key={level} value={level}>
                {t(`reception.fuel.${level}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label>
        {t('reception.vin')}
        <input
          disabled={locked || vinUnavailable}
          value={vin}
          onChange={(e) => setVin(e.target.value.toUpperCase())}
          placeholder={t('reception.vinPlaceholder')}
        />
      </label>
      <label className="reception-check">
        <input
          type="checkbox"
          disabled={locked}
          checked={vinUnavailable}
          onChange={(e) => setVinUnavailable(e.target.checked)}
        />
        {t('reception.vinUnavailable')}
      </label>

      <div className="reception-photos">
        <strong>{t('reception.photos')}</strong>
        <p className="lede">{t('reception.photosLede')}</p>
        <div className="reception-photo-grid">
          {RECEPTION_PHOTO_SIDES.map((side) => {
            const photo = photos.find((item) => item.side === side);
            const url = photo
              ? photoUrls[`${photo.bucket}:${photo.path}`]
              : null;
            const required = RECEPTION_REQUIRED_SIDES.includes(side);
            const busy = Boolean(uploadingSides[side]);
            return (
              <div
                key={side}
                className={`reception-photo-slot${busy ? ' is-uploading' : ''}`}
              >
                <span>
                  {t(`reception.side.${side}`)}
                  {required ? ' *' : ''}
                </span>
                {busy ? (
                  <div className="reception-photo-loading" aria-busy="true">
                    <span className="spinner" />
                    <em>{t('worklog.uploading')}</em>
                  </div>
                ) : url ? (
                  <button
                    type="button"
                    className="reception-photo-thumb"
                    onClick={() =>
                      setPreviewPhoto({
                        url,
                        label: t(`reception.side.${side}`),
                      })
                    }
                  >
                    <img src={url} alt={side} />
                    <span className="reception-photo-view">{t('reception.viewPhoto')}</span>
                  </button>
                ) : (
                  <em>{t('reception.addPhoto')}</em>
                )}
                {!locked && !busy ? (
                  <div className="reception-photo-actions">
                    <label className="ghost reception-photo-action">
                      {t('reception.takePhoto')}
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => {
                          uploadSide(side, e.target.files);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    <label className="ghost reception-photo-action">
                      {t('reception.uploadPhoto')}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          uploadSide(side, e.target.files);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <DamageDiagramPad
        diagram={diagram}
        disabled={locked}
        onChange={setDiagram}
        t={t}
      />

      <label>
        {t('reception.accessories')}
        <textarea
          rows={2}
          disabled={locked}
          value={accessories}
          onChange={(e) => setAccessories(e.target.value)}
          placeholder={t('reception.accessoriesPlaceholder')}
        />
      </label>
      <label>
        {t('reception.warningLights')}
        <textarea
          rows={2}
          disabled={locked}
          value={warningLights}
          onChange={(e) => setWarningLights(e.target.value)}
          placeholder={t('reception.warningPlaceholder')}
        />
      </label>

      <div className="reception-signature-block">
        <strong>{t('reception.signature')}</strong>
        <p className="lede">{t('reception.signatureLede')}</p>
        {signatureDataUrl || signatureUrl ? (
          <p className="form-ok">
            {signatureDataUrl
              ? t('reception.signatureReady')
              : t('reception.signatureOnFile')}
          </p>
        ) : null}
        {!locked ? (
          <button
            type="button"
            className="ghost"
            onClick={() => setSignatureOpen(true)}
          >
            {signatureDataUrl || signatureUrl
              ? t('reception.resign')
              : t('reception.captureSignature')}
          </button>
        ) : null}
      </div>

      <SignatureCaptureModal
        open={signatureOpen}
        t={t}
        onClose={() => setSignatureOpen(false)}
        onConfirm={(dataUrl) => setSignatureDataUrl(dataUrl)}
      />

      {!locked ? (
        <div className="row-actions">
          <button
            type="button"
            className="ghost"
            disabled={saving || Object.keys(uploadingSides).length > 0}
            onClick={() => save({ finalize: false })}
          >
            {saving ? t('common.saving') : t('reception.saveDraft')}
          </button>
          <button
            type="button"
            className="primary"
            disabled={saving || Object.keys(uploadingSides).length > 0}
            onClick={() => save({ finalize: true })}
          >
            {saving ? t('common.saving') : t('reception.finalize')}
          </button>
        </div>
      ) : null}

      {complete && profile ? (
        <p className="lede" style={{ marginTop: 12 }}>
          {t('reception.completedHint')}
        </p>
      ) : null}

      {previewPhoto ? (
        <div
          className="reception-photo-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={previewPhoto.label}
        >
          <button
            type="button"
            className="reception-photo-lightbox-backdrop"
            aria-label={t('common.cancel')}
            onClick={() => setPreviewPhoto(null)}
          />
          <div className="reception-photo-lightbox-content">
            <header className="reception-photo-lightbox-bar">
              <strong>{previewPhoto.label}</strong>
              <button
                type="button"
                className="ghost"
                onClick={() => setPreviewPhoto(null)}
              >
                {t('common.cancel')}
              </button>
            </header>
            <img src={previewPhoto.url} alt={previewPhoto.label} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
